import type {
  CatalogServiceRevision,
  DispatchMetrics,
  OperationsActiveJob,
  OperationsDispatchState,
  OperationsOrder,
  OrderStatus,
  PartnerApplicationStatus,
  PartnerApplicationSummary,
  PartnerLevel,
  QualityJobSummary,
  ServiceCatalog,
  SessionTokens,
  CommunicationMessageSummary,
  SupportCaseSummary,
  SafetyIncidentSummary,
  RetentionRunSummary,
  FinanceOperationsQueue,
  FinanceOwnerSummary,
  LedgerTransactionSummary,
  PartnerPayoutSummary,
  OperationsCommandCenterSnapshot,
  OperationsDailyReport,
  OperationsManualAction,
  OperationsManualActionPreview,
  OperationsManualIntervention,
  OperationsReasonCode,
  OperationsRuntimeFeatureFlag,
  OperationsUnifiedProfile,
  OperationsAuditEvent,
  OperationsUsersPage,
  IdentityRole,
  IdentityUserStatus,
} from "@cleany/platform-contracts";

const operationsSessionKey = "cleany.operations.session.v1";
let refreshInFlight: Promise<SessionTokens> | null = null;

export function restoreOperationsSession(): SessionTokens | null {
  const source = sessionStorage.getItem(operationsSessionKey);
  if (!source) return null;
  try {
    return JSON.parse(source) as SessionTokens;
  } catch {
    sessionStorage.removeItem(operationsSessionKey);
    return null;
  }
}

export function saveOperationsSession(session: SessionTokens): void {
  sessionStorage.setItem(operationsSessionKey, JSON.stringify(session));
  window.dispatchEvent(
    new CustomEvent("cleany:operations-session", { detail: session.accessToken }),
  );
}

export function clearOperationsSession(): void {
  sessionStorage.removeItem(operationsSessionKey);
}

export interface OtpChallenge {
  readonly challengeId: string;
  readonly expiresInSeconds: number;
}

export async function requestOperationsOtp(phone: string): Promise<OtpChallenge> {
  return request<OtpChallenge>("/api/v1/auth/otp/request", {
    method: "POST",
    body: JSON.stringify({ application: "customer", locale: "ru", phone }),
  });
}

export async function verifyOperationsOtp(
  challengeId: string,
  code: string,
): Promise<SessionTokens> {
  return request<SessionTokens>("/api/v1/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({
      challengeId,
      code,
      device: { label: "Cleany Operations", platform: "web" },
      locale: "ru",
    }),
  });
}

export function loadOperationsCatalog(token: string, locale: string): Promise<ServiceCatalog> {
  return request<ServiceCatalog>(`/api/v1/operations/catalog?locale=${locale}`, {}, token);
}

export interface OperationsUsersFilter {
  readonly query?: string;
  readonly role?: IdentityRole;
  readonly status?: IdentityUserStatus;
  readonly limit?: number;
  readonly offset?: number;
}

export function loadOperationsUsers(
  token: string,
  filter: OperationsUsersFilter = {},
): Promise<OperationsUsersPage> {
  const search = new URLSearchParams();
  if (filter.query) search.set("query", filter.query);
  if (filter.role) search.set("role", filter.role);
  if (filter.status) search.set("status", filter.status);
  search.set("limit", String(filter.limit ?? 50));
  search.set("offset", String(filter.offset ?? 0));
  return request(`/api/v1/operations/users?${search.toString()}`, {}, token);
}

export function setOperationsUserStatus(
  token: string,
  userId: string,
  status: Extract<IdentityUserStatus, "active" | "blocked">,
  reason: string,
  stepUpToken: string,
): Promise<{ readonly id: string; readonly status: IdentityUserStatus }> {
  return request(
    `/api/v1/operations/users/${encodeURIComponent(userId)}/status`,
    {
      body: JSON.stringify({ reason, status }),
      headers: { "x-cleany-step-up": stepUpToken },
      method: "PUT",
    },
    token,
  );
}

export function setOperationsStaffRole(
  token: string,
  userId: string,
  role: Extract<IdentityRole, "operator" | "supervisor" | "owner">,
  stepUpToken: string,
): Promise<void> {
  return request(
    `/api/v1/operations/staff/${encodeURIComponent(userId)}/role`,
    {
      body: JSON.stringify({ role }),
      headers: { "x-cleany-step-up": stepUpToken },
      method: "PUT",
    },
    token,
  );
}

export async function loadOperationsAudit(token: string): Promise<readonly OperationsAuditEvent[]> {
  const response = await request<{ readonly events: readonly OperationsAuditEvent[] }>(
    "/api/v1/operations/audit",
    {},
    token,
  );
  return response.events;
}

export type IntegrationProviderCode =
  | "mobizon_sms"
  | "infobip_sms"
  | "meta_whatsapp"
  | "telegram_bot"
  | "halyk_epay"
  | "twogis_maps"
  | "firebase_fcm";

export interface OperationsIntegrationProvider {
  readonly channel: "sms" | "whatsapp" | "telegram" | "payment" | "maps" | "push";
  readonly code: IntegrationProviderCode;
  readonly enabled: boolean;
  readonly healthStatus: "not_configured" | "untested" | "healthy" | "failed";
  readonly lastErrorCode: string | null;
  readonly lastTestedAt: string | null;
  readonly priority: number;
  readonly publicConfig: Readonly<Record<string, unknown>>;
  readonly secretConfigured: boolean;
  readonly updatedAt: string;
}

export interface OperationsOtpPolicy {
  readonly codeTtlSeconds: number;
  readonly maxVerifyAttempts: number;
  readonly primaryChannel: "sms" | "whatsapp";
  readonly resendAfterSeconds: number;
  readonly updatedAt: string;
  readonly whatsappFallbackToSms: boolean;
}

export interface OperationsOtpDelivery {
  readonly application: "customer" | "partner";
  readonly channel: "sms" | "whatsapp";
  readonly createdAt: string;
  readonly destinationMasked: string;
  readonly durationMs: number;
  readonly failureCode: string | null;
  readonly id: string;
  readonly providerCode: string;
  readonly providerReference: string | null;
  readonly status: "sent" | "failed";
}

export interface OperationsIntegrationsOverview {
  readonly deliveries: readonly OperationsOtpDelivery[];
  readonly policy: OperationsOtpPolicy;
  readonly providers: readonly OperationsIntegrationProvider[];
}

export function loadOperationsIntegrations(token: string): Promise<OperationsIntegrationsOverview> {
  return request("/api/v1/operations/integrations", {}, token);
}

export function updateOperationsIntegrationProvider(
  token: string,
  code: IntegrationProviderCode,
  input: {
    readonly enabled: boolean;
    readonly priority: number;
    readonly publicConfig: Readonly<Record<string, unknown>>;
    readonly reason: string;
    readonly secrets?: Readonly<Record<string, string>>;
  },
): Promise<OperationsIntegrationProvider> {
  return request(
    `/api/v1/operations/integrations/providers/${encodeURIComponent(code)}`,
    { body: JSON.stringify(input), method: "PUT" },
    token,
  );
}

export function testOperationsIntegrationProvider(
  token: string,
  code: IntegrationProviderCode,
  destination: string,
): Promise<{ readonly ok: true; readonly reference: string | null; readonly testedAt: string }> {
  return request(
    `/api/v1/operations/integrations/providers/${encodeURIComponent(code)}/test`,
    { body: JSON.stringify({ destination }), method: "POST" },
    token,
  );
}

export function updateOperationsOtpPolicy(
  token: string,
  input: Omit<OperationsOtpPolicy, "updatedAt"> & { readonly reason: string },
): Promise<OperationsOtpPolicy> {
  return request(
    "/api/v1/operations/integrations/otp-policy",
    { body: JSON.stringify(input), method: "PUT" },
    token,
  );
}

export function publishServiceRevision(
  token: string,
  serviceCode: string,
  revision: CatalogServiceRevision,
): Promise<{ readonly catalogVersion: string; readonly pricingVersion: string }> {
  return request(
    `/api/v1/operations/catalog/services/${encodeURIComponent(serviceCode)}/revisions`,
    { method: "POST", body: JSON.stringify(revision) },
    token,
  );
}

export async function loadOperationsOrders(
  token: string,
  status: OrderStatus = "searching",
): Promise<readonly OperationsOrder[]> {
  const response = await request<{ readonly orders: readonly OperationsOrder[] }>(
    `/api/v1/operations/orders?status=${status}`,
    {},
    token,
  );
  return response.orders;
}

export function loadOperationsDispatch(
  token: string,
  orderId: string,
): Promise<OperationsDispatchState> {
  return request(`/api/v1/operations/orders/${encodeURIComponent(orderId)}/dispatch`, {}, token);
}

export function restartOperationsDispatch(
  token: string,
  orderId: string,
  reason: string,
): Promise<{ readonly created: number; readonly state: string; readonly wave: number | null }> {
  return request(
    `/api/v1/operations/orders/${encodeURIComponent(orderId)}/dispatch/restart`,
    { method: "POST", body: JSON.stringify({ reason }) },
    token,
  );
}

export function manuallyAssignOrder(
  token: string,
  orderId: string,
  partnerUserId: string,
  reason: string,
): Promise<unknown> {
  return request(
    `/api/v1/operations/orders/${encodeURIComponent(orderId)}/assign`,
    { method: "POST", body: JSON.stringify({ partnerUserId, reason }) },
    token,
  );
}

export function loadDispatchMetrics(token: string): Promise<DispatchMetrics> {
  return request("/api/v1/operations/dispatch/metrics", {}, token);
}

export function loadCommandCenter(token: string): Promise<OperationsCommandCenterSnapshot> {
  return request("/api/v1/operations/command-center", {}, token);
}

export function loadUnifiedProfile(
  token: string,
  userId: string,
): Promise<OperationsUnifiedProfile> {
  return request(
    `/api/v1/operations/command-center/profiles/${encodeURIComponent(userId)}`,
    {},
    token,
  );
}

export interface OperationsManualActionInput {
  readonly action: OperationsManualAction;
  readonly targetId: string;
  readonly reasonCode: OperationsReasonCode;
  readonly reason: string;
  readonly alertId?: string;
  readonly partnerUserId?: string;
}

export function previewOperationsManualAction(
  token: string,
  input: OperationsManualActionInput,
): Promise<OperationsManualActionPreview> {
  return request(
    "/api/v1/operations/command-center/manual-actions/preview",
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export function executeOperationsManualAction(
  token: string,
  input: OperationsManualActionInput,
): Promise<OperationsManualIntervention> {
  return request(
    "/api/v1/operations/command-center/manual-actions/execute",
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export function updateRuntimeFeatureFlag(
  token: string,
  flag: OperationsRuntimeFeatureFlag,
  enabled: boolean,
  reason: string,
): Promise<OperationsRuntimeFeatureFlag> {
  return request(
    `/api/v1/operations/command-center/feature-flags/${flag.application}/${encodeURIComponent(flag.key)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ enabled, expectedRevision: flag.revision, reason }),
    },
    token,
  );
}

export function generateOperationsDailyReport(token: string): Promise<OperationsDailyReport> {
  return request("/api/v1/operations/command-center/daily-report", { method: "POST" }, token);
}

export async function loadOperationsActiveJobs(
  token: string,
): Promise<readonly OperationsActiveJob[]> {
  const response = await request<{ readonly jobs: readonly OperationsActiveJob[] }>(
    "/api/v1/operations/active-jobs",
    {},
    token,
  );
  return response.jobs;
}

export function resolveOperationsActiveJobAlert(
  token: string,
  orderId: string,
  alertId: string,
  reason: string,
): Promise<unknown> {
  return request(
    `/api/v1/operations/active-jobs/${encodeURIComponent(orderId)}/alerts/${encodeURIComponent(alertId)}/resolve`,
    { method: "POST", body: JSON.stringify({ reason }) },
    token,
  );
}

export async function loadOperationsQualityJobs(
  token: string,
): Promise<readonly QualityJobSummary[]> {
  const response = await request<{ readonly jobs: readonly QualityJobSummary[] }>(
    "/api/v1/operations/quality-jobs",
    {},
    token,
  );
  return response.jobs;
}

export function resolveOperationsQualityClaim(
  token: string,
  orderId: string,
  claimId: string,
  input: {
    readonly expectedRevision: number;
    readonly resolution: "reclean" | "compensation" | "dismissed";
    readonly reason: string;
    readonly compensationMinor?: number;
    readonly scheduledAt?: string;
  },
): Promise<{ readonly job: QualityJobSummary }> {
  return request(
    `/api/v1/operations/quality-jobs/${encodeURIComponent(orderId)}/claims/${encodeURIComponent(claimId)}/resolve`,
    {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "idempotency-key": qualityIdempotencyKey("resolve", orderId, claimId) },
    },
    token,
  );
}

export async function loadOperationsSupportCases(
  token: string,
): Promise<readonly SupportCaseSummary[]> {
  const response = await request<{ readonly cases: readonly SupportCaseSummary[] }>(
    "/api/v1/operations/support-cases",
    {},
    token,
  );
  return response.cases;
}

export function joinOperationsSupportCase(
  token: string,
  caseId: string,
): Promise<{
  readonly case: SupportCaseSummary;
  readonly messages: readonly CommunicationMessageSummary[];
}> {
  return request(
    `/api/v1/operations/support-cases/${encodeURIComponent(caseId)}/join`,
    { method: "POST" },
    token,
  );
}

export function loadOperationsSupportCase(
  token: string,
  caseId: string,
): Promise<{
  readonly case: SupportCaseSummary;
  readonly messages: readonly CommunicationMessageSummary[];
}> {
  return request(`/api/v1/operations/support-cases/${encodeURIComponent(caseId)}`, {}, token);
}

export function sendOperationsSupportMessage(
  token: string,
  caseId: string,
  body: string,
): Promise<unknown> {
  return request(
    `/api/v1/operations/support-cases/${encodeURIComponent(caseId)}/messages`,
    {
      method: "POST",
      body: JSON.stringify({ body }),
      headers: { "idempotency-key": supportIdempotencyKey("message", caseId) },
    },
    token,
  );
}

export function resolveOperationsSupportCase(
  token: string,
  caseId: string,
  resolution: string,
): Promise<{ readonly case: SupportCaseSummary }> {
  return request(
    `/api/v1/operations/support-cases/${encodeURIComponent(caseId)}/resolve`,
    { method: "POST", body: JSON.stringify({ resolution }) },
    token,
  );
}

export async function loadPartnerApplications(
  token: string,
  status?: PartnerApplicationStatus,
): Promise<readonly PartnerApplicationSummary[]> {
  const query = status ? `?status=${status}` : "";
  const response = await request<{
    readonly applications: readonly PartnerApplicationSummary[];
  }>(`/api/v1/operations/partner-applications${query}`, {}, token);
  return response.applications;
}

export function reviewPartnerApplication(
  token: string,
  applicationId: string,
  input: {
    readonly action:
      | "request_changes"
      | "schedule_control_task"
      | "approve"
      | "reject"
      | "restrict";
    readonly reason: string;
    readonly controlTaskScore?: number;
    readonly scheduledAt?: string;
    readonly reapplyAt?: string;
  },
): Promise<PartnerApplicationSummary> {
  return request(
    `/api/v1/operations/partner-applications/${encodeURIComponent(applicationId)}/review`,
    { method: "POST", body: JSON.stringify(input) },
    token,
  );
}

export function setPartnerLevel(
  token: string,
  applicationId: string,
  level: Extract<PartnerLevel, "verified" | "pro" | "restricted">,
  reason: string,
): Promise<PartnerApplicationSummary> {
  return request(
    `/api/v1/operations/partner-applications/${encodeURIComponent(applicationId)}/level`,
    { method: "POST", body: JSON.stringify({ level, reason }) },
    token,
  );
}

export async function loadSafetyIncidents(
  token: string,
): Promise<readonly SafetyIncidentSummary[]> {
  const response = await request<{ readonly incidents: readonly SafetyIncidentSummary[] }>(
    "/api/v1/operations/safety/incidents",
    {},
    token,
  );
  return response.incidents;
}

export function assignSafetyIncident(token: string, incidentId: string) {
  return request<SafetyIncidentSummary>(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/assign`,
    { method: "POST" },
    token,
  );
}

export function triageSafetyIncident(
  token: string,
  incidentId: string,
  severity: SafetyIncidentSummary["severity"],
  note: string,
) {
  return request<SafetyIncidentSummary>(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/triage`,
    { method: "POST", body: JSON.stringify({ note, severity }) },
    token,
  );
}

export function completeSafetyPlaybookStep(
  token: string,
  incidentId: string,
  stepCode: string,
  note: string,
) {
  return request<SafetyIncidentSummary>(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/playbook/${encodeURIComponent(stepCode)}/complete`,
    { method: "POST", body: JSON.stringify({ note }) },
    token,
  );
}

export function mitigateSafetyIncident(token: string, incidentId: string, note: string) {
  return request<SafetyIncidentSummary>(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/mitigate`,
    { method: "POST", body: JSON.stringify({ note }) },
    token,
  );
}

export function requestStaffStepUp(
  token: string,
  action:
    | "incident.sensitive_view"
    | "incident.restrict"
    | "incident.close"
    | "finance.refund"
    | "finance.adjustment"
    | "finance.payout"
    | "retention.run"
    | "identity.user_status_change"
    | "staff.role_change",
  subjectId: string,
): Promise<OtpChallenge> {
  return request(
    "/api/v1/operations/step-up/challenges",
    { method: "POST", body: JSON.stringify({ action, subjectId }) },
    token,
  );
}

export function loadOperationsFinanceQueue(token: string): Promise<FinanceOperationsQueue> {
  return request("/api/v1/operations/finance/queue", {}, token);
}

export function loadFinanceOwnerSummary(token: string): Promise<FinanceOwnerSummary> {
  return request("/api/v1/operations/finance/owner-summary", {}, token);
}

export function createOperationsRefund(
  token: string,
  orderId: string,
  amountMinor: number,
  reason: string,
  stepUpToken: string,
): Promise<{ readonly transaction: LedgerTransactionSummary }> {
  return request(
    `/api/v1/operations/finance/orders/${encodeURIComponent(orderId)}/refunds`,
    {
      method: "POST",
      body: JSON.stringify({ amountMinor, reason }),
      headers: {
        "idempotency-key": financeIdempotencyKey("refund", orderId),
        "x-cleany-step-up": stepUpToken,
      },
    },
    token,
  );
}

export function scheduleOperationsPayout(
  token: string,
  partnerUserId: string,
  stepUpToken: string,
): Promise<{ readonly payout: PartnerPayoutSummary }> {
  return request(
    `/api/v1/operations/finance/partners/${encodeURIComponent(partnerUserId)}/payouts`,
    {
      method: "POST",
      body: JSON.stringify({}),
      headers: {
        "idempotency-key": financeIdempotencyKey("payout", partnerUserId),
        "x-cleany-step-up": stepUpToken,
      },
    },
    token,
  );
}

export function processOperationsPayout(
  token: string,
  payoutId: string,
  stepUpToken: string,
): Promise<{ readonly payout: PartnerPayoutSummary }> {
  return request(
    `/api/v1/operations/finance/payouts/${encodeURIComponent(payoutId)}/process`,
    {
      method: "POST",
      body: JSON.stringify({}),
      headers: { "x-cleany-step-up": stepUpToken },
    },
    token,
  );
}

export function verifyStaffStepUp(
  token: string,
  challengeId: string,
  code: string,
): Promise<{ readonly stepUpToken: string; readonly expiresInSeconds: number }> {
  return request(
    "/api/v1/operations/step-up/verify",
    { method: "POST", body: JSON.stringify({ challengeId, code }) },
    token,
  );
}

export function viewSensitiveSafetyIncident(
  token: string,
  incidentId: string,
  stepUpToken: string,
  purpose: string,
): Promise<
  SafetyIncidentSummary & {
    readonly emergencyContact: {
      readonly displayName: string;
      readonly relationship: string;
      readonly phone: string;
    } | null;
    readonly evidence: readonly {
      readonly mediaId: string;
      readonly previewUrl: string;
      readonly expiresAt: string;
    }[];
  }
> {
  return request(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/sensitive-view`,
    {
      method: "POST",
      body: JSON.stringify({ purpose }),
      headers: { "x-cleany-step-up": stepUpToken },
    },
    token,
  );
}

export function restrictSafetySubject(
  token: string,
  incident: SafetyIncidentSummary,
  stepUpToken: string,
  scope: "sign_in" | "booking" | "accepting_jobs" | "payouts",
) {
  if (!incident.subjectUserId || !incident.subjectType) throw new Error("Нет субъекта ограничения");
  return request(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incident.id)}/restrictions`,
    {
      method: "POST",
      body: JSON.stringify({
        durationHours: 24,
        reasonCode: "incident_review",
        scope,
        subjectType: incident.subjectType,
        subjectUserId: incident.subjectUserId,
      }),
      headers: { "x-cleany-step-up": stepUpToken },
    },
    token,
  );
}

export function closeSafetyIncident(
  token: string,
  incidentId: string,
  stepUpToken: string,
  resolution: string,
) {
  return request<SafetyIncidentSummary>(
    `/api/v1/operations/safety/incidents/${encodeURIComponent(incidentId)}/close`,
    {
      method: "POST",
      body: JSON.stringify({ resolution }),
      headers: { "x-cleany-step-up": stepUpToken },
    },
    token,
  );
}

export async function loadRetentionRuns(token: string): Promise<readonly RetentionRunSummary[]> {
  const response = await request<{ readonly runs: readonly RetentionRunSummary[] }>(
    "/api/v1/operations/privacy/retention-runs",
    {},
    token,
  );
  return response.runs;
}

export function runRetention(
  token: string,
  stepUpToken: string,
  mode: "evaluate" | "queue_deletion",
): Promise<RetentionRunSummary> {
  return request(
    "/api/v1/operations/privacy/retention-runs",
    {
      method: "POST",
      body: JSON.stringify({ mode }),
      headers: { "x-cleany-step-up": stepUpToken },
    },
    token,
  );
}

export async function request<T>(path: string, init: RequestInit, token?: string): Promise<T> {
  const stored = token ? restoreOperationsSession() : null;
  const effectiveToken = stored?.accessToken ?? token;
  let response = await fetch(path, {
    ...init,
    headers: operationsRequestHeaders(init, effectiveToken),
  });
  if (response.status === 401 && stored?.refreshToken) {
    const refreshed = await refreshOperationsSession(stored.refreshToken);
    response = await fetch(path, {
      ...init,
      headers: operationsRequestHeaders(init, refreshed.accessToken),
    });
  }
  const payload = response.status === 204 ? null : await response.json();
  if (!response.ok) {
    const problem = payload as { title?: string; code?: string } | null;
    throw new Error(
      operationsProblemMessage(problem?.code) ??
        (problem?.title === "Unexpected platform error" ? undefined : problem?.title) ??
        problem?.code ??
        `HTTP ${response.status}`,
    );
  }
  return payload as T;
}

function operationsProblemMessage(code?: string): string | undefined {
  if (!code) return undefined;
  if (code === "integration.mobizon_base_url_invalid") {
    return "Укажите API URL Mobizon: https://api.mobizon.kz.";
  }
  if (code === "integration.test_failed") {
    return "Не удалось подключиться к провайдеру. Проверьте API URL и повторите тест.";
  }
  if (code === "integration.secret_missing") {
    return "Сначала сохраните API key провайдера, затем повторите тест.";
  }
  const mobizonPrefix = "integration.mobizon_sms.";
  if (code.startsWith(mobizonPrefix)) {
    const remoteCode = code.slice(mobizonPrefix.length);
    return `Mobizon отклонил отправку (код ${remoteCode}). Проверьте API key, баланс и зарегистрированное имя отправителя.`;
  }
  return undefined;
}

export function operationsRequestHeaders(
  init: RequestInit,
  accessToken?: string,
): Readonly<Record<string, string>> {
  const hasBody = init.body !== undefined && init.body !== null;
  return {
    accept: "application/json",
    ...(hasBody ? { "content-type": "application/json" } : {}),
    ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    ...(init.headers as Readonly<Record<string, string>> | undefined),
  };
}

async function refreshOperationsSession(refreshToken: string): Promise<SessionTokens> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const response = await fetch("/api/v1/auth/session/refresh", {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    const payload = (await response.json()) as SessionTokens | { title?: string; code?: string };
    if (!response.ok || !("accessToken" in payload)) {
      clearOperationsSession();
      throw new Error(
        ("title" in payload && payload.title) ||
          ("code" in payload && payload.code) ||
          "Сессия истекла. Войдите снова.",
      );
    }
    saveOperationsSession(payload);
    return payload;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

function qualityIdempotencyKey(action: string, orderId: string, claimId: string): string {
  return `quality:${action}:${orderId}:${claimId}:${Date.now()}`;
}

function supportIdempotencyKey(action: string, caseId: string): string {
  return `support:${action}:${caseId}:${Date.now()}`;
}

function financeIdempotencyKey(action: string, subjectId: string): string {
  return `finance:${action}:${subjectId}:${Date.now()}`;
}
